// ONE number per map (aggregated over the 5 properties), in ac/sample: index d means "as accurate
// as a d-ac/sample grid + IDW map". Pure + closed-form so it recomputes on every point drag /
// weight tweak / zone edit (debounced ~150 ms by the caller).
import C from "./accuracyIndexConstants.json";
import { polygonAreaM2, pointInPolygons, polygonsOf } from "./samplingDesign";
import { METRES_PER_DEGREE_LAT, METRES_PER_DEGREE_LNG } from "./metres";

export type AccProp = "p" | "k" | "ph" | "om" | "cec";
export const ACCURACY_PROPS: AccProp[] = ["p", "k", "ph", "om", "cec"];
// the uncorrected trend-model surface each property scores against (already served for map display)
export const MODEL_KEY: Record<AccProp, string> = {
  p: "model__uncorrected_p",
  k: "model__uncorrected_k",
  ph: "model__uncorrected_ph",
  om: "model__uncorrected_om",
  cec: "model__uncorrected_cec",
};
export const PROP_LABEL: Record<AccProp, string> = {
  p: "P",
  k: "K",
  ph: "pH",
  om: "OM",
  cec: "CEC",
};

const K = C as unknown as {
  E0: Record<AccProp, number>;
  B_c: Record<AccProp, number>;
  B_u: Record<AccProp, number>;
  M0: Record<AccProp, number>;
  c_g: Record<AccProp, number>;
  sigma_bar: Record<AccProp, number>;
  kappa_p: Record<AccProp, number>;
  R_p: Record<AccProp, number>;
  phi_p: Record<AccProp, number>;
  sigma_lab: Record<AccProp, number>;
  d0: number;
  beta: number;
  clip_sF: [number, number];
  clip_qf: [number, number];
};
const D0 = K.d0;
const BETA = K.beta;
const INV2B = 1 / (2 * BETA); // = 2 at beta 0.25

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));
const mean = (a: number[]) =>
  a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0;
const variance = (a: number[]) => {
  if (a.length < 2) return 0;
  const m = mean(a);
  return a.reduce((s, v) => s + (v - m) ** 2, 0) / a.length;
};

export interface AccuracyResult {
  index: number; // aggregate ac/sample-equivalent (mean over properties); lower = better
  byProp: Record<AccProp, number>;
  flags: string[]; // 'dense_extrapolated' | 'beyond_grid_scale' | 'low_confidence'
  beyondGridScale: boolean; // any property scored past the grid ceiling → index renders "<n>+"
}

// The ORIGIN must be SHARED across the cells and the points otherwise each set re-centres on its
// own centroid and every cell↔point distance is offset by the gap between the two centroids, so a
// corner-clumped plan scores as if it were centred on the field.
function toMeters(
  lls: { lng: number; lat: number }[],
  origin: { lat0: number; lng0: number },
): { x: number; y: number }[] {
  const kx = METRES_PER_DEGREE_LNG * Math.cos((origin.lat0 * Math.PI) / 180);
  const ky = METRES_PER_DEGREE_LAT;
  return lls.map((p) => ({
    x: (p.lng - origin.lng0) * kx,
    y: (p.lat - origin.lat0) * ky,
  }));
}

// RMS over cell centres of the distance to the nearest planned point (metres). O(cell·pts).
function hRms(
  cellXY: { x: number; y: number }[],
  ptXY: { x: number; y: number }[],
): number {
  if (!ptXY.length || !cellXY.length) return Infinity;
  let s = 0;
  for (const h of cellXY) {
    let best = Infinity;
    for (const p of ptXY) {
      const dd = (h.x - p.x) ** 2 + (h.y - p.y) ** 2;
      if (dd < best) best = dd;
    }
    s += best;
  }
  return Math.sqrt(s / cellXY.length);
}

// nearest-cell model value for each point (points sample the model surface via nearest cell centre)
function pointModelValues(
  cellXY: { x: number; y: number }[],
  mCell: number[],
  ptXY: { x: number; y: number }[],
): number[] {
  return ptXY.map((p) => {
    let best = Infinity;
    let bi = 0;
    for (let i = 0; i < cellXY.length; i++) {
      const dd = (cellXY[i].x - p.x) ** 2 + (cellXY[i].y - p.y) ** 2;
      if (dd < best) {
        best = dd;
        bi = i;
      }
    }
    return mCell[bi];
  });
}

// ruler inversion: closed-form dense power law eps²(d)=a·(d/d0)^(2β) up to the ceiling (eps²=1), then the
// mean-pinning tail eps²(d)=1+(d−dCeil)/A. Returns the ac/sample index + any flags.
function rulerInverse(
  E: number,
  prop: AccProp,
  sF: number,
  areaAcres: number,
): { index: number; flags: string[] } {
  const sigma = K.sigma_bar[prop];
  const eps2 = (E / (sigma * sF)) ** 2;
  const a = (K.E0[prop] / sigma) ** 2; // eps² at d0
  const dCeil = D0 * (1 / a) ** INV2B; // density where eps² hits 1
  const flags: string[] = [];
  if (eps2 <= 1) {
    const d = D0 * (eps2 / a) ** INV2B;
    if (d < 8.6) flags.push("dense_extrapolated");
    return { index: d, flags };
  }
  flags.push("beyond_grid_scale");
  return { index: dCeil + (eps2 - 1) * Math.max(areaAcres, 1), flags };
}

const sFOf = (prop: AccProp, mCell: number[], lowConf: boolean) =>
  lowConf
    ? 1
    : clamp(
        (K.kappa_p[prop] * Math.sqrt(variance(mCell))) / K.sigma_bar[prop],
        K.clip_sF[0],
        K.clip_sF[1],
      );
const qFOf = (mCell: number[], mPts: number[], lowConf: boolean) => {
  if (lowConf) return 1;
  const vp = variance(mPts);
  if (vp <= 0) return 1;
  // upper-clip is 1.0 — from clip_qf[1]: a designed/suggested plan should never be scored WORSE
  // than neutral coverage — the model's own error (B_c) already prices coverage. Without this cap
  // k-means cluster reps (central to each cluster → q_f slightly >1) pushed the ML index above the
  // grid for pH (B_c≈E0).
  return clamp(
    Math.sqrt((variance(mCell) + (mean(mPts) - mean(mCell)) ** 2) / vp),
    K.clip_qf[0],
    K.clip_qf[1],
  );
};

// per-property valid (non-null) model values kept with their cell index
function validModel(
  model: Partial<Record<AccProp, (number | null)[]>>,
  prop: AccProp,
): { idx: number[]; val: number[] } {
  const col = model[prop] ?? [];
  const idx: number[] = [];
  const val: number[] = [];
  col.forEach((v, i) => {
    if (v != null && Number.isFinite(v)) {
      idx.push(i);
      val.push(v);
    }
  });
  return { idx, val };
}

export interface AccuracyInput {
  kind: "ml" | "grid" | "zones" | "baseline";
  cellLngLat: { lng: number; lat: number }[]; // cell centres
  model: Partial<Record<AccProp, (number | null)[]>>; // per-property uncorrected surface, index-aligned to cells
  points?: { lng: number; lat: number }[]; // planned sample points (ml / grid)
  zones?: GeoJSON.FeatureCollection; // zone polygons (zones)
  areaAcres?: number; // field acres (only affects the beyond-ceiling tail)
}

// Score a planned map. Aggregates the 5 per-property indices to one displayed number (mean of the built props).
export function scoreAccuracy(input: AccuracyInput): AccuracyResult {
  const { kind, cellLngLat, model } = input;
  const A = input.areaAcres && input.areaAcres > 0 ? input.areaAcres : 100;
  // ONE shared projection origin (the cell/field centroid) for both cells AND points, so cross-set distances hold
  const origin = cellLngLat.length
    ? {
        lat0: mean(cellLngLat.map((p) => p.lat)),
        lng0: mean(cellLngLat.map((p) => p.lng)),
      }
    : { lat0: 0, lng0: 0 };
  const cellXY = toMeters(cellLngLat, origin);
  const ptXY = input.points ? toMeters(input.points, origin) : [];
  const flags = new Set<string>();
  const byProp = {} as Record<AccProp, number>;

  // zone → member cell indices (once, shared across props)
  let zoneMembers: { members: number[]; areaAcres: number }[] = [];
  if (kind === "zones" && input.zones) {
    const polys = input.zones.features.map((f) => ({
      polys: polygonsOf(f.geometry),
      area: polygonAreaM2(f.geometry as unknown) / 4046.86,
    }));
    zoneMembers = polys.map((p) => ({
      members: [] as number[],
      areaAcres: p.area,
    }));
    cellLngLat.forEach((h, hi) => {
      for (let zi = 0; zi < polys.length; zi++) {
        if (pointInPolygons({ lng: h.lng, lat: h.lat }, polys[zi].polys)) {
          zoneMembers[zi].members.push(hi);
          break;
        }
      }
    });
  }

  for (const prop of ACCURACY_PROPS) {
    const { idx, val } = validModel(model, prop);
    const lowConf = val.length < 8;
    // the grid index is purely A/count — it never reads the model surface, so a thin/absent model
    // must NOT flag it 'low_confidence'; the flag only means anything for the model-driven kinds.
    if (lowConf && kind !== "grid") flags.add("low_confidence");
    const sF = sFOf(prop, val, lowConf);

    if (kind === "grid") {
      // classic grid + IDW → the PLANNED density (§5.3 "a planned d-ac grid reads d"): acres per
      // placed point. For an even grid this equals the spatial-equivalent d_sp but WITHOUT the
      // boundary RMS inflation that made a 2.5-ac plan read >3 on moderate/irregular fields.
      const n = input.points?.length ?? 0;
      byProp[prop] = n > 0 ? A / n : Infinity;
    } else if (kind === "baseline") {
      const r = rulerInverse(K.B_u[prop] * sF, prop, sF, A);
      byProp[prop] = r.index;
      r.flags.forEach((f) => flags.add(f));
    } else if (kind === "ml") {
      const dsp = (hRms(cellXY, ptXY) / 25.97) ** 2;
      const mCellXY = idx.map((i) => cellXY[i]);
      const mPts = pointModelValues(mCellXY, val, ptXY);
      const qF = qFOf(val, mPts, lowConf);
      const Eml = clamp(
        K.B_c[prop] * sF * (dsp / D0) ** BETA * qF,
        0.35 * K.B_c[prop] * sF,
        K.B_u[prop] * sF,
      );
      const r = rulerInverse(Eml, prop, sF, A);
      // an ML map built from a plan is never LESS accurate than a bare grid+IDW at the SAME points
      // so cap the per-property index at that plan's planned density A/count. Without this, pH
      // (where B_c≈E0 so the model adds ~no skill) and skinny/irregular geometry — where the
      // boundary-inflated d_sp outruns the model gain — could show ML scoring WORSE than grid
      // per-property in the tooltip.
      const gridEquiv = input.points?.length
        ? A / input.points.length
        : Infinity;
      if (r.index <= gridEquiv) {
        byProp[prop] = r.index;
        r.flags.forEach((f) => flags.add(f));
      } else byProp[prop] = gridEquiv;
    } else {
      // zones (§6): 10-core composite per zone, one lab analysis per zone
      const modelByCell = model[prop] ?? [];
      let num = 0;
      let den = 0;
      for (const z of zoneMembers) {
        if (!z.members.length) continue;
        const mZ = z.members
          .map((i) => modelByCell[i])
          .filter((v): v is number => v != null && Number.isFinite(v));
        const w = variance(mZ);
        const L = Math.sqrt(z.areaAcres * 4046.86);
        const r2 =
          K.phi_p[prop] * (K.B_u[prop] * sF) ** 2 * (L / (L + K.R_p[prop]));
        const v = (w + r2) / 10 + K.sigma_lab[prop] ** 2;
        num += z.areaAcres * (w + r2 + v);
        den += z.areaAcres;
      }
      const Ezone = den > 0 ? Math.sqrt(num / den) : K.B_u[prop] * sF;
      const r = rulerInverse(Ezone, prop, sF, A);
      byProp[prop] = r.index;
      flags.add("experience_prior");
      r.flags.forEach((f) => flags.add(f));
    }
  }

  const vals = ACCURACY_PROPS.map((p) => byProp[p]).filter((v) =>
    Number.isFinite(v),
  );
  return {
    index: vals.length ? mean(vals) : Infinity,
    byProp,
    flags: [...flags],
    beyondGridScale: flags.has("beyond_grid_scale"),
  };
}

// A '+' suffix marks a value pinned past the grid-scale ceiling.
export function formatAccuracy(r: AccuracyResult, _areaAcres?: number): string {
  if (!Number.isFinite(r.index)) return "—";
  const d = r.index;
  const s = d < 10 ? d.toFixed(1) : d.toFixed(0);
  return r.beyondGridScale ? `${s}+` : s;
}
